import CalendarEventsStoreIndex from './schema/CalendarEventsStoreIndex';
import { addDays, getDateString, getTimestamp, OwaDate, startOfDay } from 'owa-datetime';
import { CalendarEvent } from 'mini-calendar-event-types';

/**
 * Class to store the event counts of each dates in a map data structure
 */
export default class CalendarEventsCountIndex implements CalendarEventsStoreIndex {
    private index: Map<string, number>;

    constructor() {
        this.index = new Map<string, number>();
    }

    /**
     * Exposed function to return the number of events for a given date from the index
     * @param date Display date
     */
    getEventsCount(date: OwaDate): number {
        const dateString: string = getDateString(date);
        return this.index.get(dateString) || 0;
    }

    /**
     * Exposed function to return the number of events for all the dates in the given date range
     * @param dateRange Date Range
     */
    getEventsCountForDateRange(inputStartDate: OwaDate, endDate: OwaDate): Map<OwaDate, number> {
        const eventsCountDateRange = new Map<OwaDate, number>();
        let startDate = inputStartDate;
        while (startDate <= endDate) {
            const eventDate: string = getDateString(startDate);
            let eventCount: number = 0;
            if (this.index.has(eventDate)) {
                eventCount = this.index.get(eventDate);
            }
            eventsCountDateRange.set(startDate, eventCount);
            startDate = addDays(startDate, 1);
        }
        return eventsCountDateRange;
    }

    /**
     * Overridden function to increase the count of the particular event for the dates it occurred
     * @param event Event to be added to the event Count
     */
    onAddCalendarEvent(event: CalendarEvent): void {
        let startDate: OwaDate = startOfDay(event.Start);
        const endDate: OwaDate = !isStartOfDay(event.End)
            ? startOfDay(event.End)
            : addDays(startOfDay(event.End), -1); // For all day events, the end date is the next day 12 midnight
        do {
            const eventDate: string = getDateString(startDate);
            if (this.index.has(eventDate)) {
                this.index.set(eventDate, this.index.get(eventDate) + 1);
            } else {
                this.index.set(eventDate, 1);
            }
            startDate = addDays(startDate, 1);
        } while (startDate <= endDate);
    }

    /**
     * Overridden function to decrease the count of the particular event for the dates it occurred
     * @param event Event to be deleted from the event Count
     */
    onDeleteCalendarEvent(event: CalendarEvent): void {
        let startDate: OwaDate = startOfDay(event.Start);
        const endDate: OwaDate = !isStartOfDay(event.End)
            ? startOfDay(event.End)
            : addDays(startOfDay(event.End), -1); // For all day events, the end date is the next day 12 midnight
        do {
            const eventDate: string = getDateString(startDate);
            if (this.index.has(eventDate)) {
                this.index.set(eventDate, this.index.get(eventDate) - 1);

                if (this.index.get(eventDate) === 0) {
                    this.index.delete(eventDate);
                }
            }
            startDate = addDays(startDate, 1);
        } while (startDate <= endDate);
    }

    clearIndex() {
        this.index = new Map<string, number>();
    }
}

function isStartOfDay(displayDate: OwaDate): boolean {
    return getTimestamp(startOfDay(displayDate)) === getTimestamp(displayDate);
}
